import { createSlice, PayloadAction, Dispatch } from "@reduxjs/toolkit";
import { ACENDEO_TEAM_ID, ATTYX_TEAM_ID, CAPTURE_NOW_TEAM_ID, FLEXPORT_TEAM_ID, TRELLUS_TEAM_ID } from "../../../cfg/const";
import { AuthorizationInfo, updateAuthorizationApiKey, getApiKeyInfo } from "../../../components/Authentication/utils";
import { groupByKeys } from "../../../core";
import { CustomDefinition, EmailValueProp, UserGroup, UserGroupMembership } from "../../../interfaces/db";
import { MultiDialerQuotaInfo } from "interfaces/subscriptions";
import { GetUserGroupResult } from "interfaces/users";
import { ScopeV2Result, AdminWriteTargetsResult, SettingsResult } from "interfaces/users";
import { UserDataResult, TeamResult, VisibleAccountsResult } from "interfaces/users";
import { CNAMRegistration } from "interfaces/twilio";
import { getServicesManager } from "../../../services";
import { loadStageConfigs } from "../analytics/stage-definitions";

export interface AuthorizationInfoState {
  value: AuthorizationInfo | null;
}
const pendingAuthorizationInfoInititalState: AuthorizationInfoState = {
  value: null,
};

export const pendingAuthorizationInfo = createSlice({
  name: 'authorizationInfo',
  initialState: pendingAuthorizationInfoInititalState,
  reducers: {
    updateAuthorizationInfo: (state, action: PayloadAction<AuthorizationInfoState>) => {
      state.value = action.payload.value;
      if (action.payload.value?.apiKey) updateAuthorizationApiKey(action.payload.value?.apiKey);
    },
  }
});

export const { updateAuthorizationInfo } = pendingAuthorizationInfo.actions;

export function reloadAuthorizationInfoInitialState(dispatch: Dispatch) {
  const result = getApiKeyInfo();
  result.then((v) => {
    if (v) {
      dispatch(pendingAuthorizationInfo.actions.updateAuthorizationInfo({ 'value': v }));
    }
  });
}
// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export type ReduxSafeUserDataResult = Omit<UserDataResult, 'created_at'> & { created_at: number; };

export function convertUserDataResultToReduxSafe(u: UserDataResult): ReduxSafeUserDataResult {
  return { ...u, 'created_at': u.created_at.valueOf() };
}

export interface UserState {
  value: ReduxSafeUserDataResult | null;
}
const userInitialResult: UserState = {
  value: null,
};

export const userSlice = createSlice({
  name: 'user',
  initialState: userInitialResult,
  reducers: {
    updateUser: (state, action: PayloadAction<ReduxSafeUserDataResult>) => {
      state.value = action.payload;
    },
  }
});

export const { updateUser } = userSlice.actions;

// Keep track of ongoing requests to prevent duplicates
let userRequestPromise: Promise<UserDataResult | null> | null = null;

export function reloadUser(dispatch: Dispatch): Promise<UserDataResult | null> {
  // If there's already a request in progress, return that promise
  if (userRequestPromise) {
    return userRequestPromise;
  }
  
  // Make the request
  userRequestPromise = getServicesManager().getUser();
  
  userRequestPromise.then(v => {
    if (v !== null) {
      dispatch((userSlice.actions.updateUser({ ...v, 'created_at': v.created_at.valueOf() })));
    }
  }).finally(() => {
    // Clear the promise so future calls can make new requests
    userRequestPromise = null;
  });
  
  return userRequestPromise;
}


export interface TeamState {
  value: TeamResult | null;
  can_text: boolean
  can_provision: boolean
}
const teamInitialResult: TeamState = {
  value: null,
  can_text: false,
  can_provision: false,
};

export const teamSlice = createSlice({
  name: 'team',
  initialState: teamInitialResult,
  reducers: {
    updateTeam: (state, action: PayloadAction<TeamResult>) => {
      state.value = action.payload;
      state.can_text = [TRELLUS_TEAM_ID, ATTYX_TEAM_ID, ACENDEO_TEAM_ID, FLEXPORT_TEAM_ID].includes(action.payload.team_id) 
      state.can_provision = [CAPTURE_NOW_TEAM_ID, TRELLUS_TEAM_ID].includes(action.payload.team_id)
    },
  }
});

export const { updateTeam } = teamSlice.actions;

// Keep track of ongoing requests to prevent duplicates
let teamRequestPromise: Promise<TeamResult | null> | null = null;

export function reloadTeam(dispatch: Dispatch): Promise<TeamResult | null> {
  // If there's already a request in progress, return that promise
  if (teamRequestPromise) {
    return teamRequestPromise;
  }
  
  // Make the request
  teamRequestPromise = getServicesManager().getTeam();
  
  teamRequestPromise.then(v => {
    if (v !== null) { 
      dispatch((teamSlice.actions.updateTeam(v))); 
      dispatch(loadStageConfigs(v.custom_stage_definition));
    }
  }).finally(() => {
    // Clear the promise so future calls can make new requests
    teamRequestPromise = null;
  });
  
  return teamRequestPromise;
}


export type ReduxSafeVisibleAccountsResult = {
  teams: TeamResult[];
  users: ReduxSafeUserDataResult[];
};

export interface VisibleAccountsState {
  value: ReduxSafeVisibleAccountsResult | null;
}
const visibleAccountsInitialState: VisibleAccountsState = {
  value: null,
};

export function convertFromReduxSafeUserResult(u: ReduxSafeUserDataResult): UserDataResult {
  return { ...u, 'created_at': new Date(u.created_at) };
}

export function convertFromReduxSafeUserState(u: UserState): UserDataResult | null {
  if (u.value === null) return null;
  return convertFromReduxSafeUserResult(u.value);
}

export function convertFromReduxSafeVisibleAccounts(v: VisibleAccountsState, filterFn?: (u: UserDataResult) => boolean): VisibleAccountsResult | null {
  if (v.value === null) return null;
  return {
    teams: v.value.teams,
    users: v.value.users.map(u => { return convertFromReduxSafeUserResult(u); }).filter(filterFn ?? (() => true))
  };
}

export const visibleAccountsSlice = createSlice({
  name: 'visibleAccounts',
  initialState: visibleAccountsInitialState,
  reducers: {
    updateVisibleAccounts: (state, action: PayloadAction<ReduxSafeVisibleAccountsResult>) => {
      state.value = {
        teams: action.payload.teams,
        users: action.payload.users.slice().sort((a, b) => a.user_name.localeCompare(b.user_name) || a.created_at - b.created_at)
      };
    },
    updateUserPriority: (state, action: PayloadAction<{ userId: string; priority: number | null }>) => {
      if (!state.value) return;
      const userIndex = state.value.users.findIndex(user => user.user_id === action.payload.userId);
      if (userIndex !== -1) {
        state.value.users[userIndex].inbound_priority = action.payload.priority;
      }
    },
    updateUserFields: (state, action: PayloadAction<{ userId: string; updates: Partial<ReduxSafeUserDataResult> }>) => {
      if (!state.value) return;
      const userIndex = state.value.users.findIndex(user => user.user_id === action.payload.userId);
      if (userIndex !== -1) {
        state.value.users[userIndex] = { ...state.value.users[userIndex], ...action.payload.updates };
      }
    },
  }
});

export const { updateVisibleAccounts, updateUserPriority, updateUserFields } = visibleAccountsSlice.actions;

// Keep track of ongoing requests to prevent duplicates
let visibleAccountsRequestPromise: Promise<VisibleAccountsResult | null> | null = null;

export async function reloadVisibleAccounts(dispatch: Dispatch): Promise<VisibleAccountsResult | null> {
  // If there's already a request in progress, return that promise
  if (visibleAccountsRequestPromise) {
    return visibleAccountsRequestPromise;
  }
  
  // Make the request
  visibleAccountsRequestPromise = getServicesManager().getVisibleAccounts();
  
  visibleAccountsRequestPromise.then(v => {
    if (v !== null) {
      dispatch(visibleAccountsSlice.actions.updateVisibleAccounts(
        {
          teams: v.teams,
          users: v.users.map(u => { return { ...u, 'created_at': u.created_at.valueOf() }; })
        }
      ));
    }
  }).finally(() => {
    // Clear the promise so future calls can make new requests
    visibleAccountsRequestPromise = null;
  });
  
  return visibleAccountsRequestPromise;
}

// Redux-safe version of CustomDefinition with serializable dates
export type ReduxSafeCustomDefinition = Omit<CustomDefinition, 'modified_as_of' | 'rebuilt_as_of'> & {
  modified_as_of: string | null;
  rebuilt_as_of: string | null;
};

export interface CustomDefinitionState {
  value: ReduxSafeCustomDefinition[] | null;
}
const customDefinitionInitialState: CustomDefinitionState = {
  value: null,
};

export const customDefinitionsSlice = createSlice({
  name: 'customDefinitions',
  initialState: customDefinitionInitialState,
  reducers: {
    updateCustomDefinitions: (state, action: PayloadAction<ReduxSafeCustomDefinition[]>) => {
      state.value = action.payload;
    },
  }
});

export const { updateCustomDefinitions } = customDefinitionsSlice.actions;

// Helper function to convert Redux-safe definitions back to original type
export function convertFromReduxSafeCustomDefinitions(definitions: ReduxSafeCustomDefinition[]): CustomDefinition[] {
  return definitions.map(def => ({
    ...def,
    modified_as_of: def.modified_as_of ? new Date(def.modified_as_of) : null,
    rebuilt_as_of: def.rebuilt_as_of ? new Date(def.rebuilt_as_of) : null
  }));
}

// Keep track of ongoing requests to prevent duplicates
let customDefinitionsRequestPromise: Promise<CustomDefinition[] | null> | null = null;

export function reloadCustomDefinitions(dispatch: Dispatch): Promise<CustomDefinition[] | null> {
  // If there's already a request in progress, return that promise
  if (customDefinitionsRequestPromise) {
    return customDefinitionsRequestPromise;
  }
  
  // Make the request
  customDefinitionsRequestPromise = getServicesManager().getCustomDefinitions();
  
  customDefinitionsRequestPromise.then(v => {
    if (v !== null) {
      // Convert Date objects to ISO strings for Redux storage
      const reduxSafeDefinitions: ReduxSafeCustomDefinition[] = v.map(def => ({
        ...def,
        modified_as_of: def.modified_as_of ? def.modified_as_of.toISOString() : null,
        rebuilt_as_of: def.rebuilt_as_of ? def.rebuilt_as_of.toISOString() : null
      }));
      dispatch(customDefinitionsSlice.actions.updateCustomDefinitions(reduxSafeDefinitions));
    }
  }).finally(() => {
    // Clear the promise so future calls can make new requests
    customDefinitionsRequestPromise = null;
  });
  
  return customDefinitionsRequestPromise;
}

export interface ScopesState {
  value: ScopeV2Result | null;
}
const scopesInitialState: ScopesState = {
  value: null,
};

export const scopesSlice = createSlice({
  name: 'scopes',
  initialState: scopesInitialState,
  reducers: {
    updateScopes: (state, action: PayloadAction<ScopeV2Result>) => {
      state.value = action.payload;
    },
  }
});

export const { updateScopes } = scopesSlice.actions;

export function reloadScopes(dispatch: Dispatch): Promise<ScopeV2Result | null> {
  const result = getServicesManager().getScopes();
  result.then(v => {
    if (v !== null) {
      dispatch(scopesSlice.actions.updateScopes(v));
    }
  });
  return result;
}

export interface AdminWriteState {
  value: AdminWriteTargetsResult | null;
}
const adminWriteInitialResult: AdminWriteState = {
  value: null,
};

export const adminWriteSlice = createSlice({
  name: 'admin_write',
  initialState: adminWriteInitialResult,
  reducers: {
    updateAdminWrite: (state, action: PayloadAction<AdminWriteTargetsResult>) => {
      state.value = action.payload;
    },
  }
});

// Keep track of ongoing requests to prevent duplicates
let adminWriteRequestPromise: Promise<AdminWriteTargetsResult | null> | null = null;

export function reloadAdminWrite(dispatch: Dispatch): Promise<AdminWriteTargetsResult | null> {
  // If there's already a request in progress, return that promise
  if (adminWriteRequestPromise) {
    return adminWriteRequestPromise;
  }
  
  // Make the request
  adminWriteRequestPromise = getServicesManager().getAdminWriteTargets();
  
  adminWriteRequestPromise.then(v => {
    if (v !== null) {
      dispatch(adminWriteSlice.actions.updateAdminWrite(v));
    }
  }).finally(() => {
    // Clear the promise so future calls can make new requests
    adminWriteRequestPromise = null;
  });
  
  return adminWriteRequestPromise;
}


export type UserGroupInfo = {
  group: UserGroup;
  directUserIds: string[];
  allUserIds: string[];
  parentIdxs: number[];
  subgroupIdxs: number[];
};

export interface UserGroupsState {
  sortedGroupInfo: UserGroupInfo[];
  groupIdToSortedIdx: Record<string, number>;
  memberships: UserGroupMembership[];
  hasLoaded: boolean;
}
const userGroupsInitialState: UserGroupsState = {
  sortedGroupInfo: [],
  groupIdToSortedIdx: {},
  memberships: [],
  hasLoaded: false,
};

export const userGroupsSlice = createSlice({
  name: 'userGroupsInfo',
  initialState: userGroupsInitialState,
  reducers: {
    updateHasLoaded: (state, action: PayloadAction<boolean>) => {
      state.hasLoaded = action.payload;
    },
    updateData: (state, action: PayloadAction<GetUserGroupResult>) => {
      // restrict to memberships in known groups
      const groupIdToGroup: Map<string, UserGroup> = new Map(action.payload.groups.map(g => [g.user_group_id, g]));
      const memberships = action.payload.memberships.filter(m => groupIdToGroup.has(m.user_group_id) && (!m.member_is_group || groupIdToGroup.has(m.member_id)));
      // set up some lookups
      const groupIdToSubgroups: Map<string, UserGroup[]> = new Map([...groupByKeys(
        memberships.filter(m => m.member_is_group),
        m => m.user_group_id,
        m => groupIdToGroup.get(m.member_id)!
      )].map(([k, v]) => [k, v.sort((g1, g2) => g1.user_group_name.localeCompare(g2.user_group_name))]));
      const groupIdToParents: Map<string, UserGroup[]> = groupByKeys(
        memberships.filter(m => m.member_is_group),
        m => m.member_id,
        m => groupIdToGroup.get(m.user_group_id)!
      );
      const groupIdToUserIds: Map<string, string[]> = groupByKeys(
        action.payload.memberships.filter(m => !m.member_is_group),
        m => m.user_group_id,
        m => m.member_id
      );
      // sort groups s.t. g1 in g2 implies g1 before g2 (but only as abstract graph nodes, not as sets of users)
      const groupIdToNumUnsortedSubgroups: Map<string, number> = new Map([...groupIdToSubgroups].map(([g, s]) => [g, s.length]));
      const inclusionSortedGroups: UserGroup[] = [...groupIdToGroup.values()].filter(g => !groupIdToNumUnsortedSubgroups.get(g.user_group_id));
      for (let i = 0; i < inclusionSortedGroups.length; ++i) { // intentionally grows as we iterate
        const g = inclusionSortedGroups[i];
        for (const parent of groupIdToParents.get(g.user_group_id) ?? []) {
          const numUnsortedSiblings = groupIdToNumUnsortedSubgroups.get(parent.user_group_id)! - 1;
          if (numUnsortedSiblings > 0) {
            groupIdToNumUnsortedSubgroups.set(parent.user_group_id, numUnsortedSiblings);
          } else {
            inclusionSortedGroups.push(parent);
          }
        }
      }
      // proceed if the sort succeeded (that is, no cycles so every group got sorted in)
      if (inclusionSortedGroups.length === groupIdToGroup.size) {
        // compute transitive-closure group members and write everything to state
        state.groupIdToSortedIdx = Object.fromEntries(inclusionSortedGroups.map((g, i) => [g.user_group_id, i]));
        state.sortedGroupInfo = inclusionSortedGroups.map(g => {
          return {
            group: g,
            directUserIds: groupIdToUserIds.get(g.user_group_id) ?? [],
            allUserIds: [],
            parentIdxs: (groupIdToParents.get(g.user_group_id) ?? []).map(p => state.groupIdToSortedIdx[p.user_group_id]).sort(),
            subgroupIdxs: (groupIdToSubgroups.get(g.user_group_id) ?? []).map(c => state.groupIdToSortedIdx[c.user_group_id]).sort(),
          };
        });
        for (const g of state.sortedGroupInfo) {
          g.allUserIds = Array.from(new Set([...g.allUserIds, ...g.directUserIds]));
          for (const parentIdx of g.parentIdxs) {
            state.sortedGroupInfo[parentIdx].allUserIds.push(...g.allUserIds);
          }
        }
        state.memberships = memberships;
      } else {
        // if the sort failed, then don't expose groups because they'd be misleading
        state.groupIdToSortedIdx = {};
        state.sortedGroupInfo = [];
        state.memberships = [];
      }
    },
  }
});


// Keep track of ongoing requests to prevent duplicates
let groupsRequestPromise: Promise<GetUserGroupResult | null> | null = null;

export function reloadGroups(dispatch: Dispatch): Promise<GetUserGroupResult | null> {
  // If there's already a request in progress, return that promise
  if (groupsRequestPromise) {
    return groupsRequestPromise;
  }
  
  // Make the request
  groupsRequestPromise = getServicesManager().getUserGroups();
  
  groupsRequestPromise.then(v => {
    if (v !== null) {
      dispatch(userGroupsSlice.actions.updateData(v));
      dispatch(userGroupsSlice.actions.updateHasLoaded(true));
    }
  }).finally(() => {
    // Clear the promise so future calls can make new requests
    groupsRequestPromise = null;
  });
  
  return groupsRequestPromise;
}export interface SettingsState {
  value: SettingsResult | null;
}
const settingsInitialState: SettingsState = {
  value: null,
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState: settingsInitialState,
  reducers: {
    updateSettings: (state, action: PayloadAction<SettingsResult>) => {
      state.value = action.payload;
    },
    updatePartialSettings: (state, action: PayloadAction<Partial<SettingsResult>>) => {
      if (!state.value) return;
      state.value = { ...state.value, ...action.payload };
    }
  }
});

export const { updateSettings, updatePartialSettings } = settingsSlice.actions;

// Keep track of ongoing requests to prevent duplicates
let userSettingsRequestPromise: Promise<SettingsResult | null> | null = null;

export function reloadUserSettings(dispatch: Dispatch): Promise<SettingsResult | null> {
  // If there's already a request in progress, return that promise
  if (userSettingsRequestPromise) {
    return userSettingsRequestPromise;
  }
  
  // Make the request
  userSettingsRequestPromise = getServicesManager().getSettings();
  
  userSettingsRequestPromise.then(v => {
    if (v !== null) {
      dispatch((settingsSlice.actions.updateSettings(v)));
    }
  }).finally(() => {
    // Clear the promise so future calls can make new requests
    userSettingsRequestPromise = null;
  });
  
  return userSettingsRequestPromise;
}
const emailValuePropsInitialResult: EmailValuePropsState = {
  value: null,
};

export const emailsValuePropsSlice = createSlice({
  name: 'email_value_props',
  initialState: emailValuePropsInitialResult,
  reducers: {
    updateEmailValueProps: (state, action: PayloadAction<EmailValueProp[]>) => {
      state.value = action.payload;
    },
  }
});

export const { updateEmailValueProps } = emailsValuePropsSlice.actions;

export function reloadEmailValueProps(dispatch: Dispatch): Promise<EmailValueProp[] | null> {
  const result = getServicesManager().getEmailValueProp();
  result.then(v => {
    if (v !== null) {
      dispatch(emailsValuePropsSlice.actions.updateEmailValueProps(v));
    }
  });
  return result;
}
export interface EmailValuePropsState {
  value: EmailValueProp[] | null;
}

// Redux-safe version of MultiDialerQuotaInfo with serializable dates
export type ReduxSafeMultiDialerQuotaInfo = Omit<MultiDialerQuotaInfo, 'period_end'> & {
  period_end: string; // ISO string
};

export interface MultiDialerQuotaState {
  value: ReduxSafeMultiDialerQuotaInfo | null;
}

const multiDialerQuotaInitialState: MultiDialerQuotaState = {
  value: null,
};

export const multiDialerQuotaSlice = createSlice({
  name: 'multiDialerQuota',
  initialState: multiDialerQuotaInitialState,
  reducers: {
    updateMultiDialerQuota: (state, action: PayloadAction<ReduxSafeMultiDialerQuotaInfo | null>) => {
      state.value = action.payload;
    },
  }
});

export const { updateMultiDialerQuota } = multiDialerQuotaSlice.actions;

// Helper function to convert MultiDialerQuotaInfo to Redux-safe format
export function toReduxSafeMultiDialerQuotaInfo(info: MultiDialerQuotaInfo): ReduxSafeMultiDialerQuotaInfo {
  return {
    ...info,
    period_end: info.period_end.toISOString()
  };
}

// Helper function to convert from Redux-safe format back to original
export function fromReduxSafeMultiDialerQuotaInfo(info: ReduxSafeMultiDialerQuotaInfo): MultiDialerQuotaInfo {
  return {
    ...info,
    period_end: new Date(info.period_end)
  };
}

export function reloadMultiDialerQuota(dispatch: Dispatch): Promise<MultiDialerQuotaInfo | null> {
  const result = getServicesManager().getMultidialerQuota();
  result.then(v => {
    if (v !== null) {
      const reduxSafeInfo = toReduxSafeMultiDialerQuotaInfo(v);
      dispatch(multiDialerQuotaSlice.actions.updateMultiDialerQuota(reduxSafeInfo));
    }
  });
  return result;
}

// CNAM Registration Redux slice
export interface CnamRegistrationsState {
  value: CNAMRegistration[] | null;
  isLoading: boolean;
  lastUpdated: number | null;
}

const cnamRegistrationsInitialState: CnamRegistrationsState = {
  value: null,
  isLoading: false,
  lastUpdated: null,
};

export const cnamRegistrationsSlice = createSlice({
  name: 'cnamRegistrations',
  initialState: cnamRegistrationsInitialState,
  reducers: {
    setCnamRegistrations: (state, action: PayloadAction<CNAMRegistration[]>) => {
      state.value = action.payload;
      state.isLoading = false;
      state.lastUpdated = Date.now();
    },
    setCnamLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    addCnamRegistration: (state, action: PayloadAction<CNAMRegistration>) => {
      if (state.value) {
        // Remove any existing registration with the same end_user.sid
        state.value = state.value.filter(reg => reg.end_user.sid !== action.payload.end_user.sid);
        // Add the new registration
        state.value.push(action.payload);
      } else {
        state.value = [action.payload];
      }
      state.lastUpdated = Date.now();
    },
    updateCnamRegistration: (state, action: PayloadAction<CNAMRegistration>) => {
      if (state.value) {
        const index = state.value.findIndex(reg => reg.end_user.sid === action.payload.end_user.sid);
        if (index !== -1) {
          state.value[index] = action.payload;
          state.lastUpdated = Date.now();
        }
      }
    },
    removeCnamRegistration: (state, action: PayloadAction<string>) => {
      if (state.value) {
        state.value = state.value.filter(reg => reg.end_user.sid !== action.payload);
        state.lastUpdated = Date.now();
      }
    },
  }
});

export const { 
  setCnamRegistrations, 
  setCnamLoading, 
  addCnamRegistration, 
  updateCnamRegistration, 
  removeCnamRegistration 
} = cnamRegistrationsSlice.actions;

export function reloadCnamRegistrations(dispatch: Dispatch, teamId: string): Promise<CNAMRegistration[] | null> {
  dispatch(setCnamLoading(true));
  const result = getServicesManager().getCnamRegistrations(teamId);
  result.then(v => {
    if (v !== null) {
      dispatch(setCnamRegistrations(v));
    } else {
      dispatch(setCnamLoading(false));
    }
  }).catch(() => {
    dispatch(setCnamLoading(false));
  });
  return result;
}

